import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Text from './Text';
import {
  arabicFonts,
  COLORS,
  englishFonts,
  fontSizes,
} from '../constants/constants';
import icons from '../constants/icons';
import ViewReverse from './ViewReverse';
import Button from './Button';
import dynamicfunction from '../utils/dynamicfunction';
import { textSizes } from '../config/config';
import { useSelector } from 'react-redux';
import Loader from './Loader';
import { formatNumbers, getContentKeyAccToLang } from '../utils/utils';
import dayjs from 'dayjs';
import SarSymbol from './SarSymbol';
import { clientSuspendedToast } from '../utils/functions';

function EnhancedTableHead(props) {
  const { headCells, isArabic, getSize, headingColor } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells?.map((headCell, index) => (
          <TableCell
            key={index}
            align={
              headCell?.align
                ? headCell?.align === 'left' && isArabic
                  ? 'right'
                  : headCell?.align
                : isArabic
                  ? 'left'
                  : 'right'
            }
            padding={'normal'}
            style={{
              backgroundColor: COLORS.table_head,
              minWidth: `${headCell?.width || 100}px`,
              maxWidth: `${headCell?.width || 100}px`,
              padding: 15,
              fontSize: getSize(),
              lineHeight: '15px',
              color: headingColor ?? COLORS.primary,
              borderBottomWidth: '2px',
              borderBottomStyle: 'solid',
              borderBottomColor: COLORS.grey,
              position: 'sticky',
              zIndex: 100,
              fontFamily: isArabic
                ? arabicFonts.almaraiRegular
                : englishFonts.shubbakMedium,
            }}
          >
            <ViewReverse className={'gap-1'} >
              {headCell?.label}
            </ViewReverse>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  const {
    heading,
    getContentKeyOrder,
    isbutton,
    rightIcon,
    btnOnClick,
    isClientSuspended,
    isArabic,
    TextWeight,
  } = props;
  return (
    <ViewReverse className={'items-center justify-between'}>
      <ViewReverse
        className={'items-center justify-between w-full mb-2.5 sm:mb-5'}
      >
        <Text
          text={heading}
          size={fontSizes.h14}
          color={'mb-1 text-black'}
          weight={TextWeight.semiBold}
          isTruncate={false}
        />
        {isbutton && (
          <Button
            text={getContentKeyOrder}
            bgColor='bg-primary'
            className={'w-fit px-8 text-center capitalize border border-orange'}
            onClick={() =>
              isClientSuspended ? clientSuspendedToast(isArabic) : btnOnClick()}
            rightIcon={rightIcon}
          />
        )}
      </ViewReverse>
    </ViewReverse>
  );
}

export default function CustomTable({
  headCells,
  data,
  heading,
  getContentKeyOrder,
  getContentKeyViewAction,
  onClick,
  btnOnClick,
  isClientSuspended = false,
  isbutton,
  rightIcon,
  onRowClick,
  maxHeight = 377,
  onPress,
  loading,
  size = 'medium',
  headingColor,
  className = '',
  showScrollBar = false,
  isDownloading = false,
  onPressDownload,
}) {
  const { isArabic, TextWeight } = dynamicfunction();

  const { fontsize, dashboardContent } = useSelector((state) => state.content);

  const getContentKey = (key, isColon = false) => {
    return getContentKeyAccToLang(dashboardContent, key, isArabic, isColon);
  };

  const handleClick = (row) => {
    onRowClick && onRowClick(row);
  };

  const getSize = () => {
    if (fontsize === textSizes.small) {
      return fontSizes.h12;
    } else if (fontsize === textSizes.medium) {
      return fontSizes.h14;
    } else if (fontsize === textSizes.large) {
      return fontSizes.h16;
    }
  };

  return (
    <div
      className={`border border-grey rounded-xl pt-4 px-5 pb-4 shadow-light ${className}`}
    >
      {heading && (
        <EnhancedTableToolbar
          heading={heading}
          TextWeight={TextWeight}
          isbutton={isbutton}
          getContentKeyOrder={getContentKeyOrder}
          rightIcon={rightIcon}
          btnOnClick={btnOnClick}
          isClientSuspended={isClientSuspended}
          isArabic={isArabic}
        />
      )}

      <div className='border border-grey rounded-xl overflow-hidden'>
        <TableContainer
          sx={{
            maxHeight: maxHeight,
            overflowY: 'auto',
          }}
        >
          <Table
            stickyHeader
            aria-labelledby='tableTitle'
            size={size}
            className={!showScrollBar ? 'hide-scrollbar' : ''}
          >
            <EnhancedTableHead
              headCells={headCells}
              isArabic={isArabic}
              getSize={getSize}
              headingColor={headingColor}
            />

            <TableBody className='relative'>
              {data?.map((row, index) => {
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(row)}
                    tabIndex={-1}
                    key={`${index} - ${row.id}`}
                    sx={{ cursor: 'pointer' }}
                  >
                    {headCells?.map((item, index) => {
                      let cellContent = row?.[item?.dataIndex];
                      item?.isDate &&
                        (item?.dateFormat ?
                          cellContent = dayjs(cellContent).format(item?.dateFormat) :
                          (cellContent = new Date(
                            cellContent
                          )?.toLocaleDateString())
                        )
                      return (
                        <TableCell
                          align={isArabic ? 'right' : 'left'}
                          key={index}
                          style={{
                            fontSize: getSize(),
                            backgroundColor: row?.color,
                            fontFamily: isArabic
                              ? arabicFonts.almaraiRegular
                              : englishFonts.shubbakMedium,
                            color: row?.textColor,
                          }}
                        >
                          {item?.children ? (
                            <div onClick={() => onPress && onPress(row)}>
                              {item?.children}
                            </div>
                          ) : item?.isActions ? (
                            <ViewReverse>
                              <Button
                                text={getContentKeyViewAction}
                                bgColor=''
                                color='text-primary'
                                className='w-fit text-center capitalize'
                                onClick={() => onClick(row)}
                                rightIcon={!isArabic && icons.viewActionbtn}
                                icon={isArabic && icons.viewActionbtn}
                              />
                            </ViewReverse>
                          ) : (
                            <Text
                              size={getSize()}
                              className={'max-w-72'}
                              color={row?.textColor}
                              align={isArabic ? 'right' : 'left'}
                              text={
                                cellContent
                                  ? item?.numeric
                                    ? cellContent == 0
                                      ? '-'
                                      : formatNumbers(Number(cellContent), item?.digits ?? 2)
                                    : cellContent || '-'
                                  : '-'
                              }
                              leftSymbol={item?.isSymbol}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {loading ? (
            <Loader width={'w-8'} height={'h-8'} className={'my-3'} />
          ) : (
            data?.length <= 0 && (
              <Text
                text={getContentKey('no_data_available')}
                className={'w-full text-center py-4'}
                isCenter={true}
              />
            )
          )}
        </TableContainer>
      </div>

      {isDownloading && (
        <>
          <div className='border-t border-grey h-1 mt-3 w-full'></div>
          <ViewReverse className='gap-2 sm:gap-3 mt-2 sm:mt-4'>
            <Button
              onClick={() => onPressDownload()}
              text={getContentKey('download_btn')}
              bgColor='bg-primary'
              className={'w-full sm:w-fit sm:px-10 rounded-lg capitalize py-2 text-center'}
            />
          </ViewReverse>
        </>
      )}
    </div>
  );
}
